import { taskLabel } from "@/lib/task-reference";
import {
  taskInstructionLabel,
  taskInstructions,
} from "@/lib/task-instructions";
import { TaskReferenceDisplay } from "@/components/task-reference-display";
import { useRef, useState } from "react";
import { ChevronDown, ChevronUp, Loader2, Plus, X } from "lucide-react";

import {
  type CarrierTrip,
  type CarrierTripStop,
  useCarrierTripDetail,
  useRemoveTasksFromTrip,
  useRemoveTripStop,
  useReorderTripStops,
} from "@/api/carrier";
import { TripProgress } from "@/components/trip-progress";
import { DetailDrawer } from "@/components/detail-drawer";
import { StatusBadge } from "@/components/status-badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { RowMenu, RowMenuItem } from "@/components/ui/row-menu";
import { hasHttpStatus } from "@/lib/api-error";
import { formatDate } from "@/lib/format";
import { operatorDisplayName } from "@/lib/operator-identity";

export type TripVehicleTarget = Pick<
  CarrierTrip,
  "id" | "vehicleId" | "dispatchCenterId"
>;
export type TripColorTarget = Pick<CarrierTrip, "id" | "color">;
export type TripDeleteTarget = Pick<CarrierTrip, "id" | "tripNumber">;

interface TripDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tripId: string;
  summary?: CarrierTrip;
  onAssignTasks: () => void;
  onAssignOperator: () => void;
  onAssignVehicle: (trip: TripVehicleTarget) => void;
  onEditColor: (trip: TripColorTarget) => void;
  onStart: () => void;
  onUnassign: () => void;
  onArchive: () => void;
  onDelete: (trip: TripDeleteTarget) => void;
}

type RemovalTarget =
  | { type: "stop"; id: string; label: string }
  | { type: "task"; id: string; label: string };

/**
 * Progressive Trip detail. The live detail response is authoritative; the
 * list summary keeps useful context visible while the detail is loading or a
 * cached detail refresh fails.
 */
export function TripDetailDialog({
  open,
  onOpenChange,
  tripId,
  summary,
  onAssignTasks,
  onAssignOperator,
  onAssignVehicle,
  onEditColor,
  onStart,
  onUnassign,
  onArchive,
  onDelete,
}: TripDetailDialogProps) {
  const tripQuery = useCarrierTripDetail(tripId);
  const reorder = useReorderTripStops();
  const removeStop = useRemoveTripStop();
  const removeTasks = useRemoveTasksFromTrip();
  const [removalTarget, setRemovalTarget] = useState<RemovalTarget | null>(
    null
  );
  const [operationError, setOperationError] = useState<string | null>(null);
  const [operationStatus, setOperationStatus] = useState<string | null>(null);
  const operationLocks = useRef(new Set<string>());

  const trip = tripQuery.data;
  const actionTrip = trip ?? summary;
  const stops = trip?.waypoints ?? [];
  const tripNumber = trip?.tripNumber ?? summary?.tripNumber;
  const status = trip?.status ?? summary?.status ?? "UNKNOWN";
  const operatorId = trip?.operatorId ?? summary?.operatorId;
  const operatorName = trip?.operator?.name
    ? operatorDisplayName({ carrierUser: { name: trip.operator.name } })
    : summary?.operator
      ? operatorDisplayName(summary.operator)
      : "Unassigned operator";
  const vehicleData = trip?.vehicle ?? summary?.vehicle;
  const vehicle = vehicleData
    ? [vehicleData.code, vehicleData.make, vehicleData.model]
        .filter(Boolean)
        .join(" · ")
    : "No vehicle";
  const center =
    trip?.depot ?? summary?.dispatchCenter ?? summary?.depot ?? null;
  const anyPending =
    reorder.isPending || removeStop.isPending || removeTasks.isPending;

  const beginOperation = (key: string) => {
    if (operationLocks.current.has(key)) return false;
    operationLocks.current.add(key);
    setOperationError(null);
    setOperationStatus(null);
    return true;
  };

  const finishOperation = (key: string) => {
    operationLocks.current.delete(key);
  };

  const move = (index: number, direction: -1 | 1) => {
    const target = stops[index + direction];
    const current = stops[index];
    if (!target || !current) return;
    const key = `reorder:${current.id}`;
    if (!beginOperation(key)) return;
    reorder.mutate(
      {
        tripId,
        waypointOrders: [
          { waypointId: current.id, sequence: target.sequence },
          { waypointId: target.id, sequence: current.sequence },
        ],
      },
      {
        onSuccess: () => setOperationStatus("Stop order updated."),
        onError: () =>
          setOperationError("Stop order could not be updated. Please retry."),
        onSettled: () => finishOperation(key),
      }
    );
  };

  const confirmRemoval = () => {
    if (!removalTarget) return;
    const target = removalTarget;
    const key = `${target.type}:${target.id}`;
    if (!beginOperation(key)) return;
    const callbacks = {
      onSuccess: () => {
        setOperationStatus(
          target.type === "stop" ? "Stop removed." : "Task removed from trip."
        );
        setRemovalTarget(null);
      },
      onError: () =>
        setOperationError(
          target.type === "stop"
            ? "Stop could not be removed. Please retry."
            : "Task could not be removed. Please retry."
        ),
      onSettled: () => finishOperation(key),
    };
    if (target.type === "stop") {
      removeStop.mutate({ tripId, waypointId: target.id }, callbacks);
    } else {
      removeTasks.mutate({ tripId, taskIds: [target.id] }, callbacks);
    }
  };

  return (
    <>
      <DetailDrawer
        open={open}
        onOpenChange={(nextOpen) => {
          if (!anyPending) onOpenChange(nextOpen);
        }}
        title={tripNumber ? `Trip TRP-${tripNumber}` : "Trip detail"}
        description={`${stops.length} stop${stops.length === 1 ? "" : "s"} · ${(trip ?? summary)?.totalTasks ?? "Unknown"} tasks`}
        widthClassName="sm:max-w-xl"
      >
        {tripQuery.isLoading && (
          <div
            role="status"
            className="flex items-center gap-2 text-sm text-muted-foreground"
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading trip…
          </div>
        )}

        {tripQuery.error && !trip && (
          <div role="alert" className="space-y-3 text-sm text-destructive">
            <p>
              {hasHttpStatus(tripQuery.error, 403)
                ? "You do not have permission to view this trip."
                : "Trip detail could not be loaded."}
            </p>
            {!hasHttpStatus(tripQuery.error, 403) && (
              <Button
                type="button"
                variant="outline"
                size="touch"
                onClick={() => tripQuery.refetch()}
              >
                Retry
              </Button>
            )}
          </div>
        )}

        {(trip || summary) && (
          <div className="space-y-5">
            {tripQuery.error && trip && (
              <div
                role="alert"
                className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
              >
                Trip detail could not be refreshed. Cached detail remains
                visible.
              </div>
            )}

            <section className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <StatusBadge status={status} />
                <span className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span
                    className="h-3 w-3 rounded-full border"
                    style={{
                      backgroundColor:
                        trip?.color ?? summary?.color ?? "transparent",
                    }}
                    aria-hidden="true"
                  />
                  {trip?.color ?? summary?.color ?? "No color"}
                </span>
              </div>
              <dl className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-x-4 gap-y-2 text-sm">
                <dt className="text-muted-foreground">Trip ID</dt>
                <dd className="break-all font-mono text-xs">{tripId}</dd>
                <dt className="text-muted-foreground">Dispatch Center</dt>
                <dd className="break-words">
                  {center?.name ?? "Unknown Dispatch Center"}
                </dd>
                <dt className="text-muted-foreground">Operator</dt>
                <dd className="break-words">{operatorName}</dd>
                <dt className="text-muted-foreground">Vehicle</dt>
                <dd className="break-words">
                  {vehicle}
                  {vehicleData?.licensePlate
                    ? ` · ${vehicleData.licensePlate}`
                    : ""}
                </dd>
                <dt className="text-muted-foreground">Created</dt>
                <dd>{formatDate(trip?.createdAt ?? summary?.createdAt)}</dd>
                <dt className="text-muted-foreground">Start</dt>
                <dd>{formatDate(trip?.startTime ?? summary?.startTime)}</dd>
                <dt className="text-muted-foreground">End</dt>
                <dd>{formatDate(trip?.endTime ?? summary?.endTime)}</dd>
                <dt className="text-muted-foreground">Estimated duration</dt>
                <dd>
                  {formatMinutes(
                    trip?.estimatedDuration ?? summary?.estimatedDuration
                  )}
                </dd>
                <dt className="text-muted-foreground">Actual duration</dt>
                <dd>
                  {formatMinutes(
                    trip?.actualDuration ?? summary?.actualDuration
                  )}
                </dd>
                <dt className="text-muted-foreground">Distance</dt>
                <dd>{formatDistance(trip?.distance ?? summary?.distance)}</dd>
                <dt className="text-muted-foreground">Progress</dt>
                <dd>
                  <TripProgress progress={trip ?? summary} />
                </dd>
                <dt className="text-muted-foreground">Start location</dt>
                <dd className="break-words">
                  {formatStructured(
                    trip?.startLocation ?? summary?.startLocation
                  )}
                  {(trip?.useCustomStartLocation ??
                    summary?.useCustomStartLocation) &&
                    " · custom"}
                </dd>
                <dt className="text-muted-foreground">End location</dt>
                <dd className="break-words">
                  {formatStructured(trip?.endLocation ?? summary?.endLocation)}
                  {(trip?.useCustomEndLocation ??
                    summary?.useCustomEndLocation) &&
                    " · custom"}
                </dd>
                <dt className="text-muted-foreground">Planned route</dt>
                <dd className="break-all text-xs">
                  {formatStructured(
                    trip?.plannedRoute ?? summary?.plannedRoute
                  )}
                </dd>
                <dt className="text-muted-foreground">Actual route</dt>
                <dd className="break-all text-xs">
                  {formatStructured(trip?.actualRoute ?? summary?.actualRoute)}
                </dd>
              </dl>
            </section>

            {operationError && (
              <p role="alert" className="text-sm text-destructive">
                {operationError}
              </p>
            )}
            {operationStatus && (
              <p role="status" className="text-sm text-muted-foreground">
                {operationStatus}
              </p>
            )}

            <section className="space-y-3 border-t pt-4">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold">Stops and tasks</h3>
                <span className="text-xs text-muted-foreground">
                  {stops.length} total
                </span>
              </div>

              {trip && stops.length === 0 && (
                <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                  No stops yet. Assign tasks to build the route.
                </p>
              )}

              <ol className="space-y-3">
                {stops.map((stop: CarrierTripStop, index) => (
                  <li key={stop.id} className="rounded-md border p-3">
                    <div className="mb-2 flex items-center gap-2">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-muted-foreground">
                          Stop {index + 1}
                        </p>
                        <p className="break-all text-xs">
                          {stop.locationKey || "No location"}
                        </p>
                      </div>
                      <div className="ml-auto flex items-center gap-1">
                        <Button
                          type="button"
                          size="icon-sm-touch"
                          variant="ghost"
                          aria-label={`Move stop ${index + 1} up`}
                          disabled={index === 0 || reorder.isPending}
                          onClick={() => move(index, -1)}
                        >
                          <ChevronUp className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          size="icon-sm-touch"
                          variant="ghost"
                          aria-label={`Move stop ${index + 1} down`}
                          disabled={
                            index === stops.length - 1 || reorder.isPending
                          }
                          onClick={() => move(index, 1)}
                        >
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          size="icon-sm-touch"
                          variant="ghost"
                          className="text-destructive"
                          aria-label={`Remove stop ${index + 1}`}
                          disabled={removeStop.isPending}
                          onClick={() =>
                            setRemovalTarget({
                              type: "stop",
                              id: stop.id,
                              label: `Stop ${index + 1}`,
                            })
                          }
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <ul className="divide-y rounded-md border">
                      {stop.tasks.map((task) => (
                        <li
                          key={task.id}
                          className="flex min-h-11 items-center gap-2 p-2 text-sm"
                        >
                          <TaskReferenceDisplay
                            task={task}
                            className="flex-1"
                          />
                          <span className="min-w-0 break-words text-muted-foreground">
                            {task.type.replace(/_/g, " ")} ·{" "}
                            {task.status.replace(/_/g, " ")} ·{" "}
                            {task.contactName ?? "No contact"}
                            {task.contactPhone && ` · ${task.contactPhone}`}
                            {task.addressLine2 &&
                              ` · Unit ${task.addressLine2}`}
                            {taskInstructions(task) &&
                              ` · ${taskInstructionLabel(task)}: ${taskInstructions(task)}`}
                          </span>
                          <Button
                            type="button"
                            size="icon-sm-touch"
                            variant="ghost"
                            aria-label={`Remove task ${taskLabel(task)} from trip`}
                            className="ml-auto shrink-0 text-destructive"
                            disabled={removeTasks.isPending}
                            onClick={() =>
                              setRemovalTarget({
                                type: "task",
                                id: task.id,
                                label: `${taskLabel(task)}`,
                              })
                            }
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ol>
            </section>

            <div className="sticky bottom-0 grid grid-cols-[1fr_44px] gap-2 border-t bg-background py-3 md:grid-cols-[1fr_32px]">
              <Button type="button" size="touch" onClick={onAssignTasks}>
                <Plus className="mr-2 h-4 w-4" />
                Assign tasks
              </Button>
              {tripNumber && (
                <RowMenu label={`Actions for TRP-${tripNumber}`} modal>
                  <RowMenuItem onClick={onAssignOperator}>
                    Assign operator
                  </RowMenuItem>
                  <RowMenuItem
                    onClick={() => actionTrip && onAssignVehicle(actionTrip)}
                  >
                    Assign vehicle
                  </RowMenuItem>
                  {status === "PLANNED" && operatorId && (
                    <RowMenuItem onClick={onStart}>Start trip</RowMenuItem>
                  )}
                  {operatorId && (
                    <RowMenuItem onClick={onUnassign}>
                      Unassign operator
                    </RowMenuItem>
                  )}
                  <RowMenuItem
                    onClick={() => actionTrip && onEditColor(actionTrip)}
                  >
                    Edit color
                  </RowMenuItem>
                  {status === "COMPLETED" && (
                    <RowMenuItem onClick={onArchive}>Archive</RowMenuItem>
                  )}
                  {status === "PLANNED" && (
                    <RowMenuItem
                      variant="destructive"
                      onClick={() => actionTrip && onDelete(actionTrip)}
                    >
                      Delete
                    </RowMenuItem>
                  )}
                </RowMenu>
              )}
            </div>
          </div>
        )}
      </DetailDrawer>

      <AlertDialog
        open={removalTarget !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !anyPending) setRemovalTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {removalTarget?.type === "stop"
                ? "Remove stop from trip?"
                : "Remove task from trip?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {removalTarget?.label ?? "This item"} will be removed from this
              Trip.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={anyPending}>
              Keep item
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={anyPending}
              onClick={(event) => {
                event.preventDefault();
                confirmRemoval();
              }}
            >
              {anyPending ? "Removing…" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function formatMinutes(value: number | null | undefined) {
  return value === null || value === undefined ? "-" : `${value} min`;
}

function formatDistance(value: number | null | undefined) {
  return value === null || value === undefined ? "-" : `${value} km`;
}

function formatStructured(value: unknown) {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return "Unavailable";
  }
}
